// ---------------------------------------------------------------------------
// fonts.js — curated Google Fonts list + dynamic loader + restore-on-load helper
//
// Foundation-owned. The editor and any feature (text panel) load fonts through
// here. `loadFontsUsedInCanvas` fixes the historical bug where a design restored
// from JSON rendered every text layer in the fallback face because the Google
// Font CSS was never fetched after loadFromJSON.
// ---------------------------------------------------------------------------

export const GOOGLE_FONTS = [
  // System / web-safe (always available, no loading needed)
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New',
  'Verdana', 'Trebuchet MS', 'Impact',
  // Google Fonts (loaded on demand)
  'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Oswald', 'Raleway', 'Nunito', 'Ubuntu', 'Merriweather',
  'Playfair Display', 'Rubik', 'Inter', 'Noto Sans', 'Work Sans',
  'Quicksand', 'Mulish', 'Barlow', 'Fira Sans', 'Karla',
  'Josefin Sans', 'DM Sans', 'Manrope', 'Space Grotesk', 'Outfit',
  'Bebas Neue', 'Archivo', 'Lexend', 'Sora', 'Red Hat Display',
  'Plus Jakarta Sans', 'Urbanist', 'Figtree', 'Onest', 'Geist',
  'Source Sans 3', 'PT Sans', 'Noto Serif', 'Lora', 'Libre Baskerville',
  'Crimson Text', 'Bitter', 'Cormorant Garamond', 'EB Garamond', 'Spectral',
  'Source Serif 4', 'Merriweather Sans', 'Vollkorn', 'Alegreya', 'Cardo',
  'Libre Franklin', 'IBM Plex Sans', 'IBM Plex Serif', 'IBM Plex Mono',
  'Fira Code', 'JetBrains Mono', 'Source Code Pro', 'Space Mono', 'Inconsolata',
  'Roboto Mono', 'Overpass Mono', 'DM Mono',
  'Anton', 'Righteous', 'Bungee', 'Permanent Marker', 'Alfa Slab One',
  'Abril Fatface', 'Passion One', 'Russo One', 'Black Ops One', 'Orbitron',
  'Press Start 2P', 'Silkscreen', 'Pixelify Sans',
  'Pacifico', 'Dancing Script', 'Caveat', 'Satisfy', 'Great Vibes',
  'Lobster', 'Sacramento', 'Cookie', 'Kaushan Script', 'Shadows Into Light',
  'Amatic SC', 'Indie Flower', 'Patrick Hand', 'Covered By Your Grace',
  'Comfortaa', 'Varela Round', 'Cabin', 'Catamaran', 'Exo 2',
  'Titillium Web', 'Dosis', 'Prompt', 'Kanit', 'Sarabun',
  'Arimo', 'Asap', 'Overpass', 'Hind', 'Mukta',
  'Signika', 'Maven Pro', 'Nunito Sans', 'Oxygen', 'Questrial',
  'Abel', 'Acme', 'Archivo Black', 'Audiowide', 'Baloo 2',
  'Bangers', 'Barlow Condensed', 'Barlow Semi Condensed',
  'Cinzel', 'Cinzel Decorative', 'Courgette', 'Crete Round',
  'Fjalla One', 'Francois One', 'Fredoka', 'Geologica',
  'Hammersmith One', 'Heebo', 'Hind Siliguri',
  'Jost', 'Kalam', 'Libre Caslon Text',
  'Lilita One', 'Monda', 'Nanum Gothic', 'Noto Sans JP',
  'Old Standard TT', 'Philosopher', 'Play', 'Pridi',
  'Rajdhani', 'Roboto Condensed', 'Roboto Slab',
  'Sawarabi Gothic', 'Secular One', 'Teko',
  'Yanone Kaffeesatz', 'Zilla Slab',
];

const SYSTEM_FONTS = new Set([
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New',
  'Verdana', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Palatino',
]);

const loadedFonts = new Set();

/**
 * Dynamically load a Google Font by injecting a <link> to Google Fonts CSS.
 * Returns a promise that resolves when the font is usable (or after a best
 * effort — never rejects, so callers can fire-and-forget).
 */
export async function loadGoogleFont(fontFamily) {
  if (!fontFamily) return;
  if (SYSTEM_FONTS.has(fontFamily)) return; // system font, already available
  if (!loadedFonts.has(fontFamily)) {
    loadedFonts.add(fontFamily);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600;1,700&display=swap`;
    document.head.appendChild(link);
  }
  // Wait for the font to actually load in the browser (idempotent — safe to
  // call again even if the <link> was already injected).
  try {
    if (document.fonts && document.fonts.load) {
      await document.fonts.load(`16px "${fontFamily}"`);
    }
  } catch (e) {
    // Ignore — the font will load eventually via the <link>.
  }
}

/**
 * Collect every fontFamily referenced by a text object on the canvas
 * (including inside groups), then await all of them and re-render.
 *
 * Call this AFTER `canvas.loadFromJSON(...)` completes: fabric renders text
 * synchronously in the load callback, before the webfont CSS has been fetched,
 * so a restored design flashes/settles in the fallback face otherwise.
 */
export async function loadFontsUsedInCanvas(canvas) {
  if (!canvas) return;
  const families = new Set();
  const collect = (obj) => {
    if (!obj) return;
    if (obj.fontFamily) families.add(obj.fontFamily);
    if (typeof obj.forEachObject === 'function') obj.forEachObject(collect);
  };
  canvas.getObjects().forEach(collect);
  if (families.size === 0) return;
  await Promise.all(Array.from(families).map((f) => loadGoogleFont(f)));
  canvas.requestRenderAll();
}
